import React, { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import useNarrowPane from '../../../../hooks/useNarrowPane';
import {ReactComponent as DownloadIcon} from '../../../../assets/img/download-icon.svg';
import { stableOrderBy } from '../../../../service/tableSorting';
import PropTypes from 'prop-types';
import { useTable, useSortBy } from 'react-table';
import ComingSoon from '../../../../components/ComingSoon';
import { leagueScorers, mergeSquadIntoPlayers } from '../../../../service/leagueTable';
import {
  StyledExport,
  StyledMobileList,
  StyledScorerCard,
  StyledPage,
  StyledBackLink,
  StyledToolbar,
  StyledTitle,
  StyledTeamSelect,
  StyledCard,
  StyledTable,
  StyledTh,
  StyledTd,
  StyledTr,
  StyledSorted,
  StyledPlayer,
  StyledEmpty,
} from './styled';

const numericSort = (rowA, rowB, id) => {
  const a = rowA.values[id] == null ? -1 : Number(rowA.values[id]);
  const b = rowB.values[id] == null ? -1 : Number(rowB.values[id]);
  return a - b;
};

const LeagueScorers = ({
  players,
  squad,
  myTeam,
  leagueId,
  teams,
  isLoading,
  onSelectPlayer,
}) => {
  const narrow = useNarrowPane();
  const teamIds = useMemo(
    () =>
      new Set(
        (teams || [])
          .map((team) => (team?._id != null ? String(team._id) : null))
          .filter(Boolean)
      ),
    [teams]
  );
  const merged = useMemo(
    () => mergeSquadIntoPlayers(players, squad, myTeam),
    [players, squad, myTeam]
  );
  const { available, rows } = useMemo(
    () => leagueScorers(merged, { leagueId, teamIds, limit: null }),
    [merged, leagueId, teamIds]
  );

  const [teamFilter, setTeamFilter] = useState(null);

  const tableRows = useMemo(() => {
    const crest = {};
    (teams || []).forEach((team) => {
      if (team?._id && team.symbol) crest[String(team._id)] = team.symbol;
    });
    return rows.map((row) => ({
      ...row,
      symbol: row.symbol || crest[String(row.teamId)] || '',
    }));
  }, [rows, teams]);

  const teamOptions = useMemo(
    () =>
      (teams || [])
        .map((team) => ({
          value: team?._id != null ? String(team._id) : '',
          label: team?.name || '',
        }))
        .filter((option) => option.value && option.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teams]
  );

  const selectedTeam = useMemo(
    () => teamOptions.find((option) => option.value === teamFilter) || null,
    [teamOptions, teamFilter]
  );

  const filteredRows = useMemo(() => {
    if (!teamFilter) return tableRows;
    return tableRows.filter((row) => String(row.teamId) === String(teamFilter));
  }, [tableRows, teamFilter]);

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'rank',
        sortType: numericSort,
        sortDescFirst: true,
      },
      {
        Header: 'Player',
        accessor: 'name',
      },
      {
        Header: 'Team',
        accessor: 'teamName',
      },
      {
        Header: 'Pos',
        accessor: 'pos',
      },
      {
        Header: 'APP',
        accessor: 'app',
        sortType: numericSort,
        sortDescFirst: true,
      },
      {
        Header: 'G',
        accessor: 'g',
        sortType: numericSort,
        sortDescFirst: true,
      },
      {
        Header: 'A',
        accessor: 'a',
        sortType: numericSort,
        sortDescFirst: true,
      },
      {
        Header: 'G+A',
        accessor: 'ga',
        sortType: numericSort,
        sortDescFirst: true,
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows: tableBody, prepareRow, setSortBy, state: {sortBy} } =
    useTable(
      {
        columns,
        data: filteredRows,
        autoResetSortBy: false,
        orderByFn: stableOrderBy,
        disableMultiSort: true,
        initialState: { sortBy: [{ id: 'g', desc: true }] },
      },
      useSortBy
    );

  const sortOptions = [{value:'rank',label:'Ranking'}, ...columns.flatMap(column=>[
    {value:column.accessor+':desc',label:column.Header+' ↓',id:column.accessor,desc:true},
    {value:column.accessor+':asc',label:column.Header+' ↑',id:column.accessor,desc:false},
  ])];
  const sortLabels = {rank:'Rank',name:'Player',teamName:'Team',pos:'Position',app:'Appearances',g:'League goals',a:'League assists',ga:'League G+A'};
  sortOptions.forEach(option=>{if(option.id)option.label=sortLabels[option.id]+(option.desc?' ↓':' ↑');});
  const hasPlayers = Array.isArray(players) && players.length > 0;
  const waiting = isLoading && !hasPlayers && !available;
  const numericIds = new Set(['rank', 'app', 'g', 'a', 'ga']);

  return (
    <StyledPage>
      <StyledBackLink to="/league">← The League</StyledBackLink>
      <StyledToolbar>
        <StyledTitle>Scorers</StyledTitle>
        {available && teamOptions.length > 0 ? (
          <StyledTeamSelect
            options={teamOptions}
            value={selectedTeam}
            onChange={(option) => setTeamFilter(option ? option.value : null)}
            placeholder="All teams"
            isClearable
            isSearchable={false}
            classNamePrefix="react-select"
            aria-label="Filter by team"
          />
        ) : null}
        {available && filteredRows.length > 0 && <StyledExport as={CSVLink}
          filename={`scorers-${leagueId || 'league'}.csv`}
          headers={columns.map((column) => ({ label: column.Header, key: column.accessor }))}
          data={tableBody.map(({ original }) => original)}>
          <DownloadIcon aria-hidden="true"/>Export CSV
        </StyledExport>}
        {narrow && available && <StyledTeamSelect className="mobile-sort" aria-label="Sort scorers" classNamePrefix="react-select" isSearchable={false} options={sortOptions} value={sortOptions.find(option=>option.value===(sortBy.length?sortBy[0].id+':'+(sortBy[0].desc?'desc':'asc'):'rank'))} onChange={option=>setSortBy(option.id?[{id:option.id,desc:option.desc}]:[])}/>}
      </StyledToolbar>
      <StyledCard>
        {waiting ? (
          <StyledEmpty>Loading…</StyledEmpty>
        ) : !available ? (
          <ComingSoon>
            <StyledEmpty>Goal and assist totals are not on this list yet</StyledEmpty>
          </ComingSoon>
        ) : tableRows.length === 0 ? (
          <StyledEmpty>No scorers this season</StyledEmpty>
        ) : filteredRows.length === 0 ? (
          <StyledEmpty>No scorers for this club</StyledEmpty>
        ) : narrow ? (
          <StyledMobileList>{tableBody.map(({original:player})=><StyledScorerCard key={player.id} type="button" onClick={()=>onSelectPlayer(player.id)} aria-label={`View ${player.name}`}>
            <span className="identity"><span className="rank">{player.rank}</span>{player.symbol && <img src={player.symbol} alt=""/>}<span className="player"><strong>{player.name}</strong><span>{player.teamName}</span></span><span className="position">{player.pos || '—'}</span></span>
            <span className="stats">{[['Matches',player.app],['League G',player.g],['League A',player.a],['G+A',player.ga]].map(([label,value])=><span key={label}><strong>{value == null || value === ''?'—':value}</strong><small>{label}</small></span>)}</span>
          </StyledScorerCard>)}</StyledMobileList>
        ) : (
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={key} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: colKey, ...colProps } = column.getHeaderProps(
                        column.getSortByToggleProps({ title: `Sort by ${column.Header}` })
                      );
                      const dir = column.isSorted
                        ? column.isSortedDesc
                          ? 'desc'
                          : 'asc'
                        : undefined;
                      return (
                        <StyledTh
                          key={colKey}
                          $numeric={numericIds.has(column.id)}
                          {...colProps}
                        >
                          {column.render('Header')}
                          <StyledSorted $dir={dir} />
                        </StyledTh>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {tableBody.map((row) => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();
                return (
                  <StyledTr
                    key={key}
                    {...rowProps}
                    onClick={() => onSelectPlayer(row.original.id)}
                  >
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      const numeric = numericIds.has(cell.column.id);
                      let value = cell.value;
                      if (cell.column.id === 'app' && (value == null || value === '')) {
                        value = '—';
                      }
                      if (cell.column.id === 'name') {
                        return (
                          <StyledTd key={cellKey} {...cellProps}>
                            <StyledPlayer>
                              {row.original.symbol ? (
                                <img src={row.original.symbol} alt="" />
                              ) : null}
                              <strong>{value || '—'}</strong>
                            </StyledPlayer>
                          </StyledTd>
                        );
                      }
                      return (
                        <StyledTd key={cellKey} $numeric={numeric} {...cellProps}>
                          {value == null || value === '' ? '—' : value}
                        </StyledTd>
                      );
                    })}
                  </StyledTr>
                );
              })}
            </tbody>
          </StyledTable>
        )}
      </StyledCard>
    </StyledPage>
  );
};

LeagueScorers.propTypes = {
  players: PropTypes.array,
  squad: PropTypes.array,
  myTeam: PropTypes.object,
  leagueId: PropTypes.string,
  teams: PropTypes.array,
  isLoading: PropTypes.bool,
  onSelectPlayer: PropTypes.func,
};

LeagueScorers.defaultProps = {
  players: [],
  squad: [],
  myTeam: null,
  leagueId: null,
  teams: [],
  isLoading: false,
  onSelectPlayer: () => {},
};

export default LeagueScorers;
